import { token } from '@atlaskit/tokens';
import { RovoThinkingBar } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBar';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import { abortDatabaseStream } from '@databases/rovo-bridge';
import React, { useCallback, useEffect, useRef } from 'react';
import { IntlProvider } from 'react-intl';

const thinkingBarStyle: React.CSSProperties = {
  position: 'fixed',
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  bottom: '36px',
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  left: '50%',
  transform: 'translateX(-50%)',
  width: '400px',
  minWidth: 'fit-content',
  zIndex: 500,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: token('radius.xlarge', '12px'),
  backgroundColor: token('color.background.input', '#fff'),
  boxShadow: token('elevation.shadow.overlay', '0 8px 12px #1e1f2126, 0 0 1px #1e1f214f'),
  paddingBlock: token('space.100', '8px'),
  paddingInline: token('space.200', '16px'),
  boxSizing: 'border-box',
};

interface DatabaseBoeThinkingBarProps {
  contentId: string;
  onDismiss: () => void;
}

export const DatabaseBoeThinkingBar = ({
  contentId,
  onDismiss,
}: DatabaseBoeThinkingBarProps) => {
  const { isStreaming } = useDatabaseStreamState();

  // Track whether isStreaming has been true at least once since mount.
  // This prevents the bar from self-dismissing immediately on mount because
  // isStreaming starts as false and only transitions true → false when the
  // AI editing phase starts and then completes.
  const hasSeenStreamingRef = useRef(false);

  useEffect(() => {
    if (isStreaming) {
      hasSeenStreamingRef.current = true;
    } else if (hasSeenStreamingRef.current) {
      // isStreaming went false after having been true — stream is complete.
      onDismiss();
    }
  }, [isStreaming, onDismiss]);

  // analyzing = prompt submitted, AI is researching (before streaming starts)
  // editing = AI is generating/populating the database
  const status = isStreaming ? 'editing' : 'analyzing';

  const handleStop = useCallback(() => {
    abortDatabaseStream(contentId);
    onDismiss();
  }, [contentId, onDismiss]);

  // RovoThinkingBar uses `useIntl` from `react-intl` (v7), whereas Databases
  // wraps its UI in react-intl-next (v5 alias). These are different React
  // contexts, so we provide a react-intl IntlProvider here.
  return (
    <IntlProvider locale="en">
      <div style={thinkingBarStyle} data-testid="database-boe-thinking-bar">
        <RovoThinkingBar status={status} onStop={handleStop} showEscapeHint={false} />
      </div>
    </IntlProvider>
  );
};
